/**
 * ZeitApp – Standalone Zeiterfassungs-App für /zeit
 *
 * Kein IMHO-Navbar, mobile-optimiert.
 * Login über /auth/login-zeit (akzeptiert alle MA mit zeigt_zeiterfassung=1).
 * nur_zeiterfassung-User können sich NUR hier einloggen, nicht im vollen IMHO-Tool.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { API_BASE } from "../api/config";
import "./ZeitApp.css";

const TOKEN_KEY = "maToken";
const getToken = () => localStorage.getItem(TOKEN_KEY);
const MONTHS_DE = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
const DAYS_DE = ["So","Mo","Di","Mi","Do","Fr","Sa"];

const api = (path, opts = {}) => {
  const token = getToken();
  const headers = { "Content-Type": "application/json", ...opts.headers };
  if (token) headers["X-MA-Token"] = token;
  return fetch(API_BASE + "/arbeitsnachweis" + path, { ...opts, headers }).then(async (r) => {
    if (!r.ok) throw Object.assign(new Error("API " + r.status), { status: r.status });
    return r.json();
  });
};

const minToHHMM = (min) => {
  if (min === null || min === undefined) return "–";
  return `${Math.floor(min / 60)}:${String(min % 60).padStart(2,"0")}`;
};
const timeStrToMin = (s) => {
  if (!s) return null;
  const [h, m] = s.split(":");
  return parseInt(h) * 60 + parseInt(m);
};
const calcH = (von, bis, pauseMin) => {
  const v = timeStrToMin(von), b = timeStrToMin(bis);
  if (v === null || b === null) return null;
  return Math.max(0, (b - v - (pauseMin || 0)) / 60);
};

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;


// Dauer im Bearbeiten-Blatt: Von/Bis minus Pause, damit man beim Ändern
// sofort sieht, was gezählt wird.
// TIME aus der Datenbank ("5:30:00", "05:30") in das von input[type=time]
// allein akzeptierte "HH:MM" bringen.
function zeitAusDb(t) {
  const m = String(t || "").match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, "0")}:${m[2]}` : "";
}

function blattDauer(t) {
  if (!t?.von || !t?.bis) return "—";
  const [vh, vm] = String(t.von).split(":").map(Number);
  const [bh, bm] = String(t.bis).split(":").map(Number);
  let min = bh * 60 + bm - (vh * 60 + vm);
  if (min < 0) min += 24 * 60;
  min -= (t.pause_min || 0);
  return min > 0 ? `${(min / 60).toFixed(2)} h` : "0.00 h";
}

export default function ZeitApp() {
  const [maToken, setMaToken] = useState(() => getToken());
  const [maInfo, setMaInfo] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Login Form
  const [kuerzel, setKuerzel] = useState("");
  const [passwort, setPasswort] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  // Tabs
  const [tab, setTab] = useState("zeit");

  // Zeiterfassung heute
  const [zeitVon, setZeitVon] = useState("");
  const [zeitBis, setZeitBis] = useState("");
  const [pauseMin, setPauseMin] = useState(0);
  const [pauseActive, setPauseActive] = useState(false);
  const pauseStartRef = useRef(null);
  const pauseAccRef = useRef(0);
  const [artHeute, setArtHeute] = useState("Arbeit");
  const [ortHeute, setOrtHeute] = useState("Homeoffice");
  const [projektHeute, setProjektHeute] = useState("");
  const [bereicheHeute, setBereicheHeute] = useState([]);
  const [extraFelder, setExtraFelder] = useState({});
  const [wasHeute, setWasHeute] = useState("");
  const [heuteReleases, setHeuteReleases] = useState([]);
  const [heuteDefault, setHeuteDefault]   = useState(null);  // Vorlagen-Pre-Fill
  // saveState: "idle" | "dirty" | "saving" | "saved" | "error"
  const [saveState, setSaveState] = useState("idle");
  const [saveError, setSaveError] = useState("");
  const [zeitLoaded, setZeitLoaded] = useState(false);
  const [showArtOrt, setShowArtOrt] = useState(false);
  const ensureHeuteRanRef = useRef(false);
  const autoSaveTimerRef = useRef(null);
  // Refs auf die jeweils AKTUELLEN State-Werte, damit der zeitverzögerte
  // Auto-Save niemals stale Closure-Daten speichert (Race-Fix).
  const latestRef = useRef({
    artHeute: "Arbeit", ortHeute: "Homeoffice", zeitVon: "", zeitBis: "",
    pauseMin: 0, wasHeute: "", projektHeute: "", bereicheHeute: [], extraFelder: {},
  });

  // Tage
  const [eintraege, setEintraege] = useState([]);
  // Bearbeiten per Tipp auf einen Tag — am Handy gab es dafür bisher keinen
  // Weg, man musste den Tag über „Heute" ansteuern.
  const [tagEdit, setTagEdit] = useState(null);   // { id, datum, art, von, bis, pause_min, arbeitsort, was }
  const [tagBusy, setTagBusy] = useState("");     // "" | "speichern" | "loeschen"
  const [tageFilter, setTageFilter] = useState("monat");
  const [jahr, setJahr] = useState(currentYear);
  const [monat, setMonat] = useState(currentMonth);

  // Statistik
  const [monate, setMonate] = useState([]);
  const [jahresuebersicht, setJahresuebersicht] = useState([]);

  // Stundensaldo kommt vom Backend (/statistik/saldo) — korrekt mit
  // Bundesland-Feiertagen und neutralem Krank. Wird gleich nach Login geladen.
  const [stundensaldo, setStundensaldo] = useState(null);

  const [uhr, setUhr] = useState(new Date());
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);

  const showToast = (msg, type = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Uhr ──
  useEffect(() => {
    const t = setInterval(() => setUhr(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // ── Pause-Timer ── (10s-Tick + Wakeup beim Wechsel auf Foreground)
  useEffect(() => {
    if (!pauseActive) return;
    const t = setInterval(() => setUhr(new Date()), 10000);
    const onVis = () => { if (document.visibilityState === "visible") setUhr(new Date()); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [pauseActive]);

  // ── Auth-Check ──
  useEffect(() => {
    const tok = getToken();
    if (!tok) { setAuthChecked(true); return; }
    setMaToken(tok);
    fetch(API_BASE + "/arbeitsnachweis/me", {
      headers: { "X-MA-Token": tok },
    }).then(async (r) => {
      if (r.ok) setMaInfo(await r.json());
      else { localStorage.removeItem(TOKEN_KEY); setMaToken(null); }
    }).catch(() => { localStorage.removeItem(TOKEN_KEY); setMaToken(null); })
      .finally(() => setAuthChecked(true));
  }, []); // eslint-disable-line

  // ── Login ──
  const handleLogin = async () => {
    setLoginError("");
    setLoginLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/login-zeit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kuerzel: kuerzel.toUpperCase().trim(), passwort }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Login fehlgeschlagen");
      }
      const data = await res.json();
      localStorage.setItem(TOKEN_KEY, data.token);
      setMaToken(data.token);
      // /me laden für Projekte/Bereiche/Felder
      const meRes = await fetch(`${API_BASE}/arbeitsnachweis/me`, {
        headers: { "X-MA-Token": data.token },
      });
      if (meRes.ok) setMaInfo(await meRes.json());
    } catch (e) {
      setLoginError(e.message);
    }
    setLoginLoading(false);
  };

  // ── Logout ──
  const handleLogout = async () => {
    const tok = getToken();
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        headers: { "x-ma-token": tok || "" },
      });
    } catch {}
    localStorage.removeItem(TOKEN_KEY);
    setMaToken(null);
    setMaInfo(null);
    setZeitLoaded(false);
    ensureHeuteRanRef.current = false;
  };

  // ── Datum, das die Erfassungsmaske gerade bearbeitet ──
  const [editDate, setEditDate] = useState(() => new Date().toISOString().slice(0, 10));

  // Rundet ein "HH:MM" auf die nächste viertel Stunde (oder eine Date-Instanz).
  const _roundHHMM = (hhmm) => {
    if (!hhmm) return "";
    const [h, m] = String(hhmm).split(":").map(Number);
    if (isNaN(h) || isNaN(m)) return "";
    const total = h * 60 + m;
    const snap = Math.round(total / 15) * 15;
    return `${String(Math.floor(snap / 60) % 24).padStart(2,"0")}:${String(snap % 60).padStart(2,"0")}`;
  };

  // ── Eintrag für editDate laden ──
  // Bekommt das Datum als Parameter mitgegeben, damit Datums-Wechsel im
  // Picker sofort den richtigen Tag laden — ohne auf den State-Refresh zu
  // warten. Setzt vorher die States zurück, damit alte Werte nicht stehen
  // bleiben, wenn der neue Tag leer ist.
  const loadHeute = useCallback(async (datum) => {
    const d_param = datum || editDate || new Date().toISOString().slice(0, 10);
    const heute_iso = new Date().toISOString().slice(0, 10);
    const isToday = d_param === heute_iso;
    // Reset — leer; Default-Vorbelegung erfolgt unten, falls der Tag in der
    // DB leer ist.
    setZeitVon(""); setZeitBis("");
    setPauseMin(0); pauseAccRef.current = 0;
    setArtHeute("Arbeit"); setOrtHeute(""); setWasHeute("");
    setProjektHeute(""); setBereicheHeute([]); setExtraFelder({});
    setHeuteDefault(null);
    try {
      const d = await api(`/heute?datum=${d_param}`);
      const e = d.eintrag;
      const eintragHasContent = !!(e && (e.von || e.bis || e.was || e.arbeitsort || e.felder_json));
      if (e) {
        if (e.von) setZeitVon(zeitAusDb(e.von));
        if (e.bis) setZeitBis(zeitAusDb(e.bis));
        if (e.pause) { const pm = timeStrToMin(zeitAusDb(e.pause)); setPauseMin(pm || 0); pauseAccRef.current = pm || 0; }
        if (e.art) setArtHeute(e.art);
        if (e.arbeitsort) setOrtHeute(e.arbeitsort);
        if (e.was) setWasHeute(e.was);
        setProjektHeute(e.projekt_kuerzel || "");
        let parsedExtra = {};
        if (e.felder_json) {
          try {
            const fj = JSON.parse(e.felder_json);
            setBereicheHeute(fj.bereiche || []);
            parsedExtra = fj.extra || {};
          } catch {}
        }
        // Rückwärtskompat: epic aus DB-Spalte in extraFelder mergen
        if (e.epic && !parsedExtra.epic) parsedExtra = { ...parsedExtra, epic: e.epic };
        setExtraFelder(parsedExtra);
      }
      setHeuteReleases(d.releases || []);
      // Default-Vorbelegung der Stechuhr: 00:00 / 00:00. Damit zeigt die
      // Maske beim ersten Laden klar „nichts erfasst". Bei Tap auf einen
      // Input wird vor dem Picker-Öffnen auf die aktuell gerundete Zeit
      // gesetzt (siehe onPointerDown am Input), damit der iOS-Picker auf
      // der richtigen Zeit aufgeht und nicht bei 00:00.
      if (!eintragHasContent) {
        setZeitVon("00:00");
        setZeitBis("00:00");
      }
      // Wenn der Tag leer ist UND kein Wochentag-Default existiert, frag
      // historische Vorauswahl (letzte 12 Wochen, gleicher Wochentag) und
      // ÜBERNIMM die Werte direkt — keine Banner-Frage mehr, der User kann
      // sie eh einzeln wieder wegklicken.
      if (!eintragHasContent && !d.default) {
        try {
          const v = await api(`/heute/vorschlag?datum=${d_param}`);
          if (v && v.samples_n >= 3) {
            if (v.arbeitsort && !d_param /* heute */) setOrtHeute(v.arbeitsort);
            if (v.arbeitsort) setOrtHeute(v.arbeitsort);
            if ((v.bereiche_vorschlag || []).length) setBereicheHeute(v.bereiche_vorschlag);
            if ((v.felder_vorschlag || []).length) {
              const extras = {};
              for (const k of v.felder_vorschlag) extras[k] = "1";
              setExtraFelder(extras);
            }
          }
        } catch {}
      } else if (d.default) {
        // Wochentag-Default ebenfalls direkt übernehmen (keine Banner-Frage)
        const def = d.default;
        if (def.art)               setArtHeute(def.art);
        if (def.arbeitsort)        setOrtHeute(def.arbeitsort);
        if (def.projekt_kuerzel)   setProjektHeute(def.projekt_kuerzel);
        if (def.bereiche?.length)  setBereicheHeute(def.bereiche);
      }
      setZeitLoaded(true);
      setSaveState("idle");
    } catch {}
  }, [editDate]);

  useEffect(() => {
    if (!authChecked || !maToken) return;
    if (ensureHeuteRanRef.current) return;
    ensureHeuteRanRef.current = true;
    api("/ensure-heute", { method: "POST" }).catch(() => {});
    loadHeute(editDate);
  }, [authChecked, maToken, loadHeute, editDate]);

  useEffect(() => {
    if (!authChecked || !maToken) return;
    if (tab === "zeit" && !zeitLoaded) loadHeute(editDate);
  }, [tab, zeitLoaded, loadHeute, authChecked, maToken, editDate]);

  // Wenn der User das Datum im Picker wechselt, neu laden.
  useEffect(() => {
    if (!authChecked || !maToken || !zeitLoaded) return;
    loadHeute(editDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDate]);

  // ── Tage laden ──
  const loadEintraege = useCallback(async () => {
    setLoading(true);
    try {
      let url = "/arbeitszeit?limit=500";
      if (tageFilter !== "alles") url += `&jahr=${jahr}`;
      if (tageFilter === "monat") url += `&monat=${monat}`;
      const d = await api(url);
      setEintraege(d.eintraege || []);
    } catch {}
    finally { setLoading(false); }
  }, [tageFilter, jahr, monat]);

  // ── Statistik laden ──
  const loadStatistik = useCallback(async () => {
    try {
      const [j, m_] = await Promise.all([
        api("/statistik/jahresuebersicht"),
        api(`/statistik/monatsübersicht?jahr=${jahr}`),
      ]);
      setJahresuebersicht(j.jahre || []);
      setMonate(m_.monate || []);
    } catch {}
  }, [jahr]);

  useEffect(() => { if (!authChecked || !maToken) return; if (tab === "tage") loadEintraege(); }, [tab, loadEintraege, authChecked, maToken]);
  useEffect(() => { if (!authChecked || !maToken) return; if (tab === "statistik") loadStatistik(); }, [tab, loadStatistik, authChecked, maToken]);

  // ── Anträge (Urlaub/Krank) — gleiche Endpoints wie die Desktop-Seite ──
  const [antraege, setAntraege] = useState([]);
  const [antrForm, setAntrForm] = useState({ art: "Urlaub", von: "", bis: "", anmerkung: "" });
  const [antrSaving, setAntrSaving] = useState(false);
  const loadAntraege = useCallback(async () => {
    try { const d = await api("/antraege"); setAntraege(d.antraege || []); } catch {}
  }, []);
  useEffect(() => { if (!authChecked || !maToken) return; if (tab === "antraege") loadAntraege(); }, [tab, loadAntraege, authChecked, maToken]);

  const submitAntrag = async () => {
    if (!antrForm.von || !antrForm.bis) { showToast("Bitte Von und Bis ausfüllen", "error"); return; }
    setAntrSaving(true);
    try {
      await api("/antraege", { method: "POST", body: JSON.stringify(antrForm) });
      setAntrForm({ art: "Urlaub", von: "", bis: "", anmerkung: "" });
      await loadAntraege();
      showToast("Antrag gestellt ✓");
    } catch (e) {
      showToast("Fehler: " + e.message, "error");
    } finally { setAntrSaving(false); }
  };
  const zurueckziehenAntrag = async (id) => {
    if (!window.confirm("Diesen Antrag wirklich zurückziehen?")) return;
    try {
      await api(`/antraege/${id}`, { method: "DELETE" });
      await loadAntraege();
      showToast("Antrag zurückgezogen");
    } catch (e) { showToast("Fehler: " + e.message, "error"); }
  };
  // Saldo + Resturlaub unmittelbar nach Login einmal laden (Header-Anzeige).
  useEffect(() => {
    if (!authChecked || !maToken) return;
    api(`/statistik/saldo?jahr=${currentYear}`).then(setStundensaldo).catch(() => {});
  }, [authChecked, maToken]); // eslint-disable-line

  // ── Speichern ──
  // Liest die aktuellen Werte aus dem Ref, NICHT aus Closure-Variablen.
  // So speichert ein verzögerter Auto-Save immer den neuesten Stand und
  // wir vermeiden die Race-Condition, die schnelle Eingaben verlieren ließ.
  const doSave = useCallback(async () => {
    const v = latestRef.current;
    const isArbeit = v.artHeute === "Arbeit";
    const fj = (v.bereicheHeute.length > 0 || Object.keys(v.extraFelder).length > 0)
      ? JSON.stringify({ bereiche: v.bereicheHeute, extra: v.extraFelder })
      : null;
    setSaveState("saving");
    setSaveError("");
    try {
      await api("/heute", {
        method: "POST",
        body: JSON.stringify({
          datum: v.editDate || undefined,   // ohne Datum schriebe der Server auf heute
          art: v.artHeute,
          arbeitsort: isArbeit ? v.ortHeute : null,
          von: isArbeit ? (v.zeitVon || null) : null,
          bis: isArbeit ? (v.zeitBis || null) : null,
          pause_min: isArbeit ? v.pauseMin : 0,
          was: v.wasHeute || null,
          projekt_kuerzel: isArbeit ? (v.projektHeute || null) : null,
          felder_json: isArbeit ? fj : null,
        }),
      });
      setSaveState("saved");
      // nach 2.5 s wieder Idle, damit der Indikator nicht verharrt
      setTimeout(() => setSaveState((s) => (s === "saved" ? "idle" : s)), 2500);
    } catch (e) {
      setSaveState("error");
      setSaveError(e.message || "Speichern fehlgeschlagen");
    }
  }, []);

  // Manuelle Freigabe / „Tag freigeben" — erzwingt sofortiges Speichern,
   //   wendet die Pflicht-Pause an (bei > 5 h Arbeit mindestens 30 min) und
   //   zeigt einen Toast.
   const handleSave = () => {
     const v = latestRef.current;
     if (v.artHeute === "Arbeit" && v.zeitVon && v.zeitBis) {
       const dauerMin = (() => {
         const [vh, vm] = v.zeitVon.split(":").map(Number);
         const [bh, bm] = v.zeitBis.split(":").map(Number);
         let mins = bh * 60 + bm - (vh * 60 + vm);
         if (mins < 0) mins += 24 * 60;
         return mins - (v.pauseMin || 0);
       })();
       if (dauerMin > 5 * 60 && (v.pauseMin || 0) < 30) {
         setPauseMin(30);
         pauseAccRef.current = 30;
         showToast("Bei mehr als 5 h Arbeit ist eine 30-Min-Pause gesetzlich vorgeschrieben — automatisch gesetzt.");
         // doSave verwendet latestRef (Ref-Pattern), aber der setState wirkt
         // erst beim nächsten Render. Wir geben React kurz Zeit, latestRef zu
         // aktualisieren, bevor wir speichern.
         setTimeout(() => doSave(), 50);
         return;
       }
     }
     doSave();
   };

  // ── Tag aus der Liste bearbeiten (Handy) ────────────────────────────────
  // Öffnet den angetippten Tag in einem Blatt. Gespeichert wird über denselben
  // Weg wie „Heute" (POST /heute mit Datum), damit Pflicht-Pause und
  // Stundenberechnung identisch greifen.
  const tagOeffnen = (e) => {
    // Die Datenbank liefert TIME als "5:30:00" — ohne führende Null und mit
    // Sekunden. Ein input[type=time] nimmt ausschliesslich "HH:MM" und bleibt
    // bei allem anderen kommentarlos leer. Genau das war die leere Anzeige.
    const hhmm = (t) => {
      const m = String(t || "").match(/(\d{1,2}):(\d{2})/);
      return m ? `${m[1].padStart(2, "0")}:${m[2]}` : "";
    };
    const pauseMinAus = (p) => {
      if (!p) return 0;
      const [h, m] = String(p).split(":").map(Number);
      return (h || 0) * 60 + (m || 0);
    };
    // Angekreuzte Bereiche und Zusatzfelder stecken im felder_json — ohne sie
    // wären beim Bearbeiten alle Häkchen leer und gingen beim Speichern verloren.
    let bereiche = [];
    let extra = {};
    try {
      const fj = e.felder_json ? JSON.parse(e.felder_json) : null;
      if (fj) {
        bereiche = Array.isArray(fj.bereiche) ? fj.bereiche : [];
        extra = fj.extra && typeof fj.extra === "object" ? fj.extra : {};
      }
    } catch { /* kaputtes JSON darf das Bearbeiten nicht blockieren */ }

    setTagEdit({
      id: e.id,
      datum: String(e.datum || "").slice(0, 10),
      art: e.art || "Arbeit",
      von: hhmm(e.von),
      bis: hhmm(e.bis),
      pause_min: pauseMinAus(e.pause),
      arbeitsort: e.arbeitsort || "",
      was: e.was || "",
      bereiche,
      extra,
    });
  };

  const tagSpeichern = async () => {
    if (!tagEdit) return;
    const istArbeit = tagEdit.art === "Arbeit";
    setTagBusy("speichern");
    try {
      const fj = (tagEdit.bereiche?.length > 0 || Object.keys(tagEdit.extra || {}).length > 0)
        ? JSON.stringify({ bereiche: tagEdit.bereiche || [], extra: tagEdit.extra || {} })
        : null;
      await api("/heute", {
        method: "POST",
        body: JSON.stringify({
          datum: tagEdit.datum,
          art: tagEdit.art,
          arbeitsort: istArbeit ? (tagEdit.arbeitsort || null) : null,
          von: istArbeit ? (tagEdit.von || null) : null,
          bis: istArbeit ? (tagEdit.bis || null) : null,
          pause_min: istArbeit ? (tagEdit.pause_min || 0) : 0,
          was: tagEdit.was || null,
          felder_json: istArbeit ? fj : null,
        }),
      });
      setTagEdit(null);
      showToast("Tag gespeichert");
      loadEintraege();
      // Steht derselbe Tag im Heute-Tab offen, dort nachziehen
      if (tagEdit.datum === editDate) loadHeute(editDate);
    } catch (err) {
      showToast(err.message || "Speichern fehlgeschlagen");
    } finally {
      setTagBusy("");
    }
  };

  // Leert den Eintrag, behält den Tag aber als Zeile: Ein echtes Löschen
  // würde den Tag ganz aus der Liste nehmen — er soll wie ein noch nicht
  // erfasster Werktag dastehen.
  const tagLeeren = async () => {
    if (!tagEdit?.datum) return;
    if (!window.confirm(
      `Eintrag vom ${tagEdit.datum.split("-").reverse().join(".")} leeren?\n\n`
      + `Der Tag bleibt in der Liste, nur die Angaben werden entfernt.`)) return;
    setTagBusy("loeschen");
    try {
      await api("/heute", {
        method: "POST",
        body: JSON.stringify({ datum: tagEdit.datum, leeren: true }),
      });
      setTagEdit(null);
      showToast("Eintrag geleert");
      loadEintraege();
      if (tagEdit.datum === editDate) loadHeute(editDate);
    } catch (err) {
      showToast(err.message || "Leeren fehlgeschlagen");
    } finally {
      setTagBusy("");
    }
  };

  // Auto-Save: 1,5 s nach letzter Änderung. Speichert immer den aktuellsten
  // State (Ref-Pattern), nicht den zum Zeitpunkt der Änderung eingefrorenen.
  const scheduleAutoSave = useCallback(() => {
    setSaveState("dirty");
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => { doSave(); }, 1500);
  }, [doSave]);

  // Ref aktuell halten + bei jedem Render Auto-Save planen, sobald geladen.
  useEffect(() => {
    // editDate muss mit in den Ref: Sonst speichert doSave() immer auf HEUTE,
    // auch wenn oben ein anderer Tag gewählt ist — die Anzeige meldete dann
    // „gespeichert", während der bearbeitete Tag unverändert blieb.
    latestRef.current = { artHeute, ortHeute, zeitVon, zeitBis, pauseMin,
                          wasHeute, projektHeute, bereicheHeute, extraFelder,
                          editDate };
    if (!zeitLoaded) return;
    scheduleAutoSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artHeute, ortHeute, zeitVon, zeitBis, pauseMin, wasHeute,
      projektHeute, bereicheHeute, extraFelder, editDate]);

  // Rundet auf nächste viertel Stunde (was näher ist). 09:28 → 09:30, 09:38 → 09:45.
  const roundTo15 = (d) => {
    const total = d.getHours() * 60 + d.getMinutes();
    const snapped = Math.round(total / 15) * 15;
    const h = Math.floor(snapped / 60) % 24;
    const m = snapped % 60;
    return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`;
  };
  // HH:MM + dauer_minuten → neue HH:MM
  const addMinutesToTime = (hhmm, minutes) => {
    if (!hhmm) return "";
    const [h, m] = hhmm.split(":").map(Number);
    const total = h * 60 + m + minutes;
    const hh = Math.floor((total / 60) % 24);
    const mm = total % 60;
    return `${String(hh).padStart(2,"0")}:${String(mm).padStart(2,"0")}`;
  };
  const handleStart = () => setZeitVon(roundTo15(uhr));
  const handleSchluss = () => setZeitBis(roundTo15(uhr));

  // Default-Schluss = Start + Tagessoll. Greift NUR wenn der User Start
  // aktiv ÄNDERT (nicht 00:00) und Schluss noch leer/00:00 ist.
  useEffect(() => {
    if (!zeitLoaded) return;
    if (artHeute !== "Arbeit") return;
    if (!zeitVon || zeitVon === "00:00") return;
    if (zeitBis && zeitBis !== "00:00") return;
    const regel = parseFloat(stundensaldo?.regelarbeitszeit_h) || 8;
    const wochensoll = parseFloat(stundensaldo?.wochensoll_h);
    const tageWoche = parseInt(stundensaldo?.arbeitstage_woche, 10) || 5;
    let dauerH = (!isNaN(wochensoll) && wochensoll > 0) ? wochensoll / tageWoche : regel;
    // Plausibilität: wenn die Berechnung < 1 h ergibt (typisch bei
    // fehlkonfigurierten wochensoll_h-Werten), Standard-Tagessoll 2 h
    // verwenden statt eines Drittel-Stunden-Defaults.
    if (dauerH < 1) dauerH = 2;
    setZeitBis(addMinutesToTime(zeitVon, Math.round(dauerH * 60)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zeitVon]);
  const handlePauseToggle = () => {
    if (!pauseActive) {
      pauseStartRef.current = Date.now();
      setPauseActive(true);
    } else {
      const elapsed = Math.floor((Date.now() - (pauseStartRef.current || Date.now())) / 60000);
      pauseAccRef.current += elapsed;
      setPauseMin(pauseAccRef.current);
      setPauseActive(false);
    }
  };

  const arbStunden = calcH(zeitVon, zeitBis, pauseActive
    ? pauseMin + Math.floor((Date.now() - (pauseStartRef.current || Date.now())) / 60000)
    : pauseMin);

  const heute = uhr;
  const uhrzeitStr = `${String(heute.getHours()).padStart(2,"0")}:${String(heute.getMinutes()).padStart(2,"0")}`;
  const datumStr = `${DAYS_DE[heute.getDay()]}, ${heute.getDate()}. ${MONTHS_DE[heute.getMonth()]} ${heute.getFullYear()}`;

  // ── Login Screen ──
  if (!authChecked) return <div className="za-loading">Lade…</div>;

  if (!maToken || !maInfo) {
    return (
      <div className="za-login-wrap">
        <form className="za-login-box" onSubmit={e => { e.preventDefault(); handleLogin(); }}>
          <div className="za-login-logo">⏱</div>
          <h1 className="za-login-title">Zeiterfassung</h1>
          {loginError && <div className="za-login-error">{loginError}</div>}
          <input
            className="za-input"
            type="text"
            name="username"
            placeholder="Kürzel (z.B. LBO)"
            value={kuerzel}
            onChange={e => setKuerzel(e.target.value.toUpperCase())}
            autoCapitalize="characters"
            autoComplete="username"
            inputMode="text"
          />
          <input
            className="za-input"
            type="password"
            name="password"
            placeholder="Passwort"
            value={passwort}
            onChange={e => setPasswort(e.target.value)}
            autoComplete="current-password"
          />
          <button type="submit" className="za-btn za-btn-login" disabled={loginLoading}>
            {loginLoading ? "…" : "Anmelden"}
          </button>
        </form>
      </div>
    );
  }

  // ── Haupt-App ──
  return (
    <div className="za-app">
      {/* Header */}
      <div className="za-header">
        <div className="za-header-title">Zeiterfassung</div>
        <div className="za-header-user">
          {tab === "zeit" && saveState && saveState !== "idle" && (
            <span className={`za-save-pill za-save-pill-${saveState}`}>
              {saveState === "saving" ? "Speichert…"
                : saveState === "saved"  ? "✓ Gespeichert"
                : saveState === "error"  ? "⚠ Fehler"
                : "Auto-Save in 1,5 s"}
            </span>
          )}
          <span className="za-user-badge">{maInfo.kuerzel}</span>
          <button className="za-logout-btn" onClick={handleLogout}>↩</button>
        </div>
      </div>

      {/* Tabs (oben auf Desktop, Bottom-Bar auf Mobile via CSS) */}
      <div className="za-tabs-wrap">
        <div className="za-tabs">
          {[["zeit","⏱","Heute"],["tage","📅","Tage"],["antraege","📨","Anträge"],["statistik","📊","Stats"]].map(([id, icon, label]) => (
            <button key={id} className={`za-tab ${tab === id ? "active" : ""}`} onClick={() => setTab(id)}>
              <span className="za-tab-icon">{icon}</span>
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab: Heute ── */}
      {tab === "zeit" && (
        <div className="za-content">
          <div className="za-desktop-grid">
            {/* Eine harmonische Erfassungs-Sektion: Header (Datum / Art-Pille
                / Uhr) + Stechuhr-Grid (Start ↔ Dauer, Schluss ↔ Freigeben). */}
            <div className="za-desktop-full">
              <div className="za-erfassung">
                <div className="za-erfassung-head">
                  <input type="date" className="za-date-input"
                    value={editDate}
                    onChange={e => setEditDate(e.target.value)}
                    max="2999-12-31" />
                  <div className="za-artort-pill-wrap">
                    <button type="button" className="za-artort-pill"
                      onClick={() => setShowArtOrt(v => !v)}>
                      <span className="za-artort-pill-text">{artHeute}</span>
                      <span className="za-artort-chevron">{showArtOrt ? "▴" : "▾"}</span>
                    </button>
                    {showArtOrt && (
                      <div className="za-artort-overlay">
                        <div className="za-field">
                          <label className="za-label">Art</label>
                          <div className="za-art-grid">
                            {["Arbeit","Krank","Urlaub"].map(a => (
                              <button key={a} type="button"
                                className={`za-art-btn ${artHeute === a ? "active" : ""}`}
                                onClick={() => setArtHeute(a)}>{a}</button>
                            ))}
                          </div>
                        </div>
                        {artHeute === "Arbeit" && (
                          <div className="za-field">
                            <label className="za-label">Ort</label>
                            <div className="za-art-grid">
                              {(maInfo?.orte?.length > 0 ? maInfo.orte : ["Homeoffice","Bremen"]).map(o => (
                                <button key={o} type="button"
                                  className={`za-art-btn ${ortHeute === o ? "active" : ""}`}
                                  onClick={() => { setOrtHeute(o); setShowArtOrt(false); }}>{o}</button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <span className="za-uhr">{uhrzeitStr}</span>
                </div>

                {artHeute === "Arbeit" && (
                  <div className="za-erfassung-grid">
                    {/* Start + Dauer (oben). onPointerDown setzt vor dem
                        Picker-Öffnen die aktuelle Zeit, sonst öffnet iOS
                        den Picker auf 00:00. */}
                    <div className="za-stechuhr-row">
                      <div className="za-stechuhr-label">Start</div>
                      <input type="time" step="900" className="za-stechuhr-input-big"
                        value={zeitVon}
                        onPointerDown={() => { if (!zeitVon || zeitVon === "00:00") setZeitVon(roundTo15(new Date())); }}
                        onChange={e => setZeitVon(_roundHHMM(e.target.value))} />
                    </div>
                    <div className="za-dauer-box">
                      <div className="za-stechuhr-label">Dauer</div>
                      <div className="za-dauer-value">
                        {arbStunden !== null && arbStunden > 0 ? `${arbStunden.toFixed(2)} h` : "0.00 h"}
                      </div>
                    </div>
                    {/* Schluss + Freigeben (unten). */}
                    <div className="za-stechuhr-row">
                      <div className="za-stechuhr-label">Schluss</div>
                      <input type="time" step="900" className="za-stechuhr-input-big"
                        value={zeitBis}
                        onPointerDown={() => {
                          if (!zeitBis || zeitBis === "00:00") {
                            const base = (zeitVon && zeitVon !== "00:00") ? zeitVon : roundTo15(new Date());
                            setZeitBis(addMinutesToTime(base, 120));
                          }
                        }}
                        onChange={e => setZeitBis(_roundHHMM(e.target.value))} />
                    </div>
                    <button
                      type="button"
                      className="za-btn-freigeben-side"
                      onClick={() => {
                        handleSave();
                        showToast("Tag freigegeben — Auto-Save bleibt aktiv");
                      }}
                      disabled={saveState === "saving"}>
                      {saveState === "saving" ? "…" : (<><span style={{fontSize: 14, lineHeight: 1}}>✓</span><span>Freigeben</span></>)}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {artHeute === "Arbeit" && (
              <>

                {/* Projekt */}
                {maInfo?.projekte?.length > 0 && (
                  <div className="za-field">
                    <label className="za-label">Projekt</label>
                    <select className="za-select" value={projektHeute} onChange={e => setProjektHeute(e.target.value)}>
                      <option value="">– kein –</option>
                      {maInfo.projekte.map(p => (
                        <option key={p.p_kuerzel || p.label} value={p.p_kuerzel || p.label}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Bereiche */}
                {maInfo?.bereiche?.length > 0 && (
                  <div className="za-field">
                    <label className="za-label">Bereiche</label>
                    <div className="za-bereiche">
                      {maInfo.bereiche.map(b => (
                        <label key={b.id} className="za-bereich-check">
                          <input type="checkbox"
                            checked={bereicheHeute.includes(b.bereich_label)}
                            onChange={e => {
                              if (e.target.checked) setBereicheHeute(p => [...p, b.bereich_label]);
                              else setBereicheHeute(p => p.filter(x => x !== b.bereich_label));
                            }}
                          />
                          {b.bereich_label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dynamische Felder aus ma_felder — gruppiert nach Präfix (Section).
                    Felder, deren erstes Wort bei ≥2 Feldern vorkommt, bilden eine
                    eigene Section („Saugen P1/P2/P3" → Section „Saugen"). Der Rest
                    landet in „Sonstiges". */}
                {maInfo?.felder?.length > 0 && (() => {
                  const sectionFor = (name) => (name || "").trim().split(/\s+/)[0] || "Sonstiges";
                  const counts = {};
                  maInfo.felder.forEach(f => {
                    const s = sectionFor(f.feld_name);
                    counts[s] = (counts[s] || 0) + 1;
                  });
                  const sections = {};
                  maInfo.felder.forEach(f => {
                    const s = counts[sectionFor(f.feld_name)] >= 2 ? sectionFor(f.feld_name) : "Sonstiges";
                    if (!sections[s]) sections[s] = [];
                    sections[s].push(f);
                  });
                  const sectionOrder = Object.keys(sections).filter(s => s !== "Sonstiges").concat(sections.Sonstiges ? ["Sonstiges"] : []);
                  return (
                  <div className="za-field za-felder">
                  {sectionOrder.map(sectionName => (
                  <div key={sectionName} className="za-felder-section">
                    <div className="za-felder-section-title">{sectionName}</div>
                    <div className="za-felder-grid">
                    {sections[sectionName].map(f => {
                      const optionen = (() => { try { return f.optionen ? JSON.parse(f.optionen) : []; } catch { return []; } })();
                      const currentArr = (() => {
                        const v = extraFelder[f.feld_name];
                        if (Array.isArray(v)) return v;
                        if (typeof v === "string" && v.startsWith("[")) {
                          try { const a = JSON.parse(v); return Array.isArray(a) ? a : []; } catch { return []; }
                        }
                        return [];
                      })();
                      const toggleMs = (opt) => {
                        const next = currentArr.includes(opt) ? currentArr.filter(x => x !== opt) : [...currentArr, opt];
                        setExtraFelder(p => ({ ...p, [f.feld_name]: JSON.stringify(next) }));
                      };
                      const triCycle = () => {
                        const cur = String(extraFelder[f.feld_name] || "");
                        const next = cur === "" ? "0.5" : cur === "0.5" ? "1" : "";
                        setExtraFelder(p => ({ ...p, [f.feld_name]: next }));
                      };
                      const triState = String(extraFelder[f.feld_name] || "");
                      return (
                      <div key={f.feld_name} className={`za-felder-item ${(f.feld_typ === "checkbox" || f.feld_typ === "tristate") ? "za-felder-check" : ""}`}>
                        {f.feld_typ === "checkbox" ? (
                          <label className="za-bereich-check">
                            <input type="checkbox"
                              checked={!!extraFelder[f.feld_name]}
                              onChange={e => setExtraFelder(p => ({ ...p, [f.feld_name]: e.target.checked ? "1" : "" }))}
                            />
                            {f.feld_label || f.feld_name}
                          </label>
                        ) : f.feld_typ === "tristate" ? (
                          <button type="button" className={`za-tristate za-tristate-${triState === "1" ? "voll" : triState === "0.5" ? "halb" : "leer"}`}
                            onClick={triCycle}>
                            <span className="za-tristate-mark">{triState === "1" ? "✓" : triState === "0.5" ? "½" : "·"}</span>
                            {f.feld_label || f.feld_name}
                          </button>
                        ) : (
                          <>
                            <label className="za-label">{f.feld_label || f.feld_name}</label>
                            {f.feld_typ === "select" ? (
                              <select className="za-select"
                                value={extraFelder[f.feld_name] || ""}
                                onChange={e => setExtraFelder(p => ({ ...p, [f.feld_name]: e.target.value }))}>
                                <option value="">– wählen –</option>
                                {optionen.map(o => <option key={o} value={o}>{o}</option>)}
                              </select>
                            ) : f.feld_typ === "multiselect" ? (
                              <div className="za-multiselect">
                                {optionen.length === 0 && <span className="za-muted">(Admin: bitte Optionen pflegen)</span>}
                                {optionen.map(o => (
                                  <button type="button" key={o}
                                    className={`za-multi-btn ${currentArr.includes(o) ? "active" : ""}`}
                                    onClick={() => toggleMs(o)}>{o}</button>
                                ))}
                              </div>
                            ) : (
                              <input
                                type={(f.feld_typ === "number" || f.feld_typ === "decimal") ? "number" : "text"}
                                step={f.feld_typ === "decimal" ? "0.5" : undefined}
                                inputMode={f.feld_typ === "decimal" ? "decimal" : f.feld_typ === "number" ? "numeric" : undefined}
                                className="za-input"
                                value={extraFelder[f.feld_name] || ""}
                                onChange={e => setExtraFelder(p => ({ ...p, [f.feld_name]: e.target.value }))}
                              />
                            )}
                          </>
                        )}
                      </div>
                      );
                    })}
                    </div>
                  </div>
                  ))}
                  </div>
                  );
                })()}

              </>
            )}

            {/* Heutige Releases (nur bei Vollzugang sichtbar) */}
            {heuteReleases.length > 0 && artHeute === "Arbeit" && (
              <div className="za-desktop-full">
                <div className="za-releases">
                  <div className="za-releases-label">Releases heute</div>
                  <div className="za-releases-list">
                    {heuteReleases.map((r) => (
                      <span key={r.id} className="za-release-chip">
                        v{r.release_version}{r.was ? ` – ${r.was}` : ""}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Was gemacht — auch bei Krank/Urlaub erlaubt (z. B. „grippal", „Familie") */}
            <div className="za-field za-desktop-full">
              <label className="za-label">
                {artHeute === "Krank" ? "Anmerkung (optional)"
                 : artHeute === "Urlaub" ? "Anmerkung (optional)"
                 : "Was habe ich gemacht"}
              </label>
              <textarea className="za-textarea" rows={2}
                placeholder={artHeute === "Arbeit" ? "Tätigkeiten, Aufgaben…" : ""}
                value={wasHeute} onChange={e => setWasHeute(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Tage ── */}
      {tab === "tage" && (
        <div className="za-content">
          <div className="za-filter-row">
            <select className="za-select" value={tageFilter} onChange={e => setTageFilter(e.target.value)}>
              <option value="monat">Monat</option>
              <option value="jahr">Jahr</option>
              <option value="alles">Alles</option>
            </select>
            {tageFilter !== "alles" && (
              <select className="za-select" value={jahr} onChange={e => setJahr(+e.target.value)}>
                {[2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            {tageFilter === "monat" && (
              <select className="za-select" value={monat} onChange={e => setMonat(+e.target.value)}>
                {MONTHS_DE.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
            )}
          </div>

          {loading && <div className="za-loading">Lade…</div>}
          {!loading && eintraege.length > 0 && (() => {
            const regel = parseFloat(stundensaldo?.regelarbeitszeit_h) || 0;
            const heute = new Date().toISOString().slice(0, 10);
            const past = eintraege.filter(e => (e.datum || "").slice(0, 10) <= heute);
            const future = eintraege.length - past.length;
            const s = past.reduce((acc, e) => {
              if (e.art === "Arbeit" || e.art === "Ist" || e.art === "Plan") {
                acc.arbeit += 1;
                acc.arbeit_h += parseFloat(e.arbeitszeit_h) || 0;
              } else if (e.art === "Urlaub")   acc.urlaub += 1;
              else if (e.art === "Krank")      acc.krank += 1;
              else if (e.art === "Feiertag")   acc.feiertag += 1;
              return acc;
            }, { arbeit:0, arbeit_h:0, urlaub:0, krank:0, feiertag:0 });
            const bezahlt = s.urlaub + s.krank + s.feiertag;
            const total = s.arbeit_h + bezahlt * regel;
            return (
              <div className="za-tages-summe">
                <div className="za-tages-summe-haupt">
                  <span className="za-tages-summe-label">Σ Stunden</span>
                  <span className="za-tages-summe-val">{total.toFixed(1)} h</span>
                </div>
                <div className="za-tages-summe-detail">
                  <span>{s.arbeit} Arbeit</span>
                  {s.urlaub > 0   && <span>· {s.urlaub} Urlaub</span>}
                  {s.krank > 0    && <span>· {s.krank} Krank</span>}
                  {s.feiertag > 0 && <span>· {s.feiertag} Feiertag</span>}
                  {future > 0     && <span>· {future} vorgemerkt</span>}
                </div>
              </div>
            );
          })()}
          <div className="za-tage-list">
            {eintraege.length === 0 && !loading && <p className="za-empty">Keine Einträge</p>}
            {eintraege.filter(e => !e.release_version).map(e => {
              // Wochentag + deutsches Datum aus dem Datum berechnen —
              // die wotag-Spalte in der DB ist unzuverlässig (oft 0)
              const _d = String(e.datum || "").slice(0, 10);
              const _parts = _d.split("-");
              const _datumDe = _parts.length === 3 ? `${_parts[2]}.${_parts[1]}.${_parts[0]}` : _d;
              const _wotagDe = _parts.length === 3 ? DAYS_DE[new Date(_d + "T12:00:00").getDay()] : "";
              // Zeiten "5:00:00" → "05:00"
              const _hhmm = (t) => {
                if (!t) return "";
                const [h, m] = String(t).split(":");
                return `${String(h).padStart(2, "0")}:${m}`;
              };
              return (
              <div key={e.id} role="button" tabIndex={0}
                   className={`za-tag-card za-tag-${(e.art || "").toLowerCase()} za-tag-klickbar`}
                   onClick={() => tagOeffnen(e)}
                   onKeyDown={(ev) => { if (ev.key === "Enter") tagOeffnen(e); }}>
                <div className="za-tag-header">
                  <span className="za-tag-datum">{_wotagDe}, {_datumDe}</span>
                  <span className="za-tag-art">{e.art}</span>
                </div>
                {e.art === "Arbeit" && (
                  <div className="za-tag-body">
                    {e.von && e.bis && <span>{_hhmm(e.von)} – {_hhmm(e.bis)}</span>}
                    {e.arbeitszeit_h && <span>{parseFloat(e.arbeitszeit_h).toFixed(1)} h</span>}
                    {e.arbeitsort && <span>{e.arbeitsort}</span>}
                  </div>
                )}
                {e.was && <div className="za-tag-was">{e.was}</div>}
              </div>
              );
            })}
          </div>

          {/* Bearbeiten-Blatt: öffnet sich beim Tippen auf einen Tag */}
          {tagEdit && (
            <div className="za-blatt-hg" onClick={() => !tagBusy && setTagEdit(null)}>
              <div className="za-blatt" onClick={(ev) => ev.stopPropagation()}>
                <div className="za-blatt-kopf">
                  <span className="za-blatt-titel">{tagEdit.datum.split("-").reverse().join(".")}</span>
                  <button className="za-blatt-zu" onClick={() => setTagEdit(null)}>✕</button>
                </div>

                <div className="za-blatt-inhalt">
                <div className="za-field">
                  <label className="za-label">Art</label>
                  <div className="za-art-grid">
                    {["Arbeit", "Krank", "Urlaub"].map(a => (
                      <button key={a} type="button"
                        className={`za-art-btn ${tagEdit.art === a ? "active" : ""}`}
                        onClick={() => setTagEdit(t => ({ ...t, art: a }))}>{a}</button>
                    ))}
                  </div>
                </div>

                {tagEdit.art === "Arbeit" && (
                  <>
                    {/* Von/Bis nebeneinander, Pause eigene Zeile — zu dritt
                        überlappten die Beschriftungen auf schmalen Geräten. */}
                    <div className="za-blatt-zeiten">
                      <div className="za-field">
                        <label className="za-label">Von</label>
                        <input type="time" step="900" className="za-input za-blatt-zeit" value={tagEdit.von}
                          onChange={ev => setTagEdit(t => ({ ...t, von: ev.target.value }))} />
                      </div>
                      <div className="za-field">
                        <label className="za-label">Bis</label>
                        <input type="time" step="900" className="za-input za-blatt-zeit" value={tagEdit.bis}
                          onChange={ev => setTagEdit(t => ({ ...t, bis: ev.target.value }))} />
                      </div>
                    </div>

                    <div className="za-blatt-zeile2">
                      <div className="za-field">
                        <label className="za-label">Pause (Min)</label>
                        <input type="number" min="0" step="5" className="za-input za-blatt-zeit"
                          value={tagEdit.pause_min}
                          onChange={ev => setTagEdit(t => ({ ...t, pause_min: +ev.target.value || 0 }))} />
                      </div>
                      <div className="za-field">
                        <label className="za-label">Dauer</label>
                        <div className="za-blatt-dauer">{blattDauer(tagEdit)}</div>
                      </div>
                    </div>

                    <div className="za-field">
                      <label className="za-label">Ort</label>
                      <div className="za-art-grid">
                        {(maInfo?.orte?.length > 0 ? maInfo.orte : ["Homeoffice", "Bremen"]).map(o => (
                          <button key={o} type="button"
                            className={`za-art-btn ${tagEdit.arbeitsort === o ? "active" : ""}`}
                            onClick={() => setTagEdit(t => ({ ...t, arbeitsort: o }))}>{o}</button>
                        ))}
                      </div>
                    </div>

                    {/* Angekreuzte Bereiche — fehlten hier bisher ganz und
                        gingen beim Speichern verloren. */}
                    {maInfo?.bereiche?.length > 0 && (
                      <div className="za-field">
                        <label className="za-label">Bereiche</label>
                        <div className="za-bereiche">
                          {maInfo.bereiche.map(b => (
                            <label key={b.id} className="za-bereich-check">
                              <input type="checkbox"
                                checked={(tagEdit.bereiche || []).includes(b.bereich_label)}
                                onChange={ev => setTagEdit(t => ({
                                  ...t,
                                  bereiche: ev.target.checked
                                    ? [...(t.bereiche || []), b.bereich_label]
                                    : (t.bereiche || []).filter(x => x !== b.bereich_label),
                                }))} />
                              {b.bereich_label}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Dieselben Sektionen wie in „Heute" (Saugen, Sonstiges …):
                        nach dem ersten Wort gruppiert, sobald es mehrfach vorkommt. */}
                    {maInfo?.felder?.length > 0 && (() => {
                      const sect = (n) => (n || "").trim().split(/\s+/)[0] || "Sonstiges";
                      const zaehler = {};
                      maInfo.felder.forEach(f => { const s = sect(f.feld_name); zaehler[s] = (zaehler[s] || 0) + 1; });
                      const gruppen = {};
                      maInfo.felder.forEach(f => {
                        const s = zaehler[sect(f.feld_name)] >= 2 ? sect(f.feld_name) : "Sonstiges";
                        (gruppen[s] = gruppen[s] || []).push(f);
                      });
                      const reihenfolge = Object.keys(gruppen).filter(s => s !== "Sonstiges")
                        .concat(gruppen.Sonstiges ? ["Sonstiges"] : []);
                      const setzen = (name, wert) => setTagEdit(t => {
                        const ex = { ...(t.extra || {}) };
                        if (wert === false || wert === "" || wert == null) delete ex[name];
                        else ex[name] = wert;
                        return { ...t, extra: ex };
                      });
                      return (
                        <div className="za-field za-felder">
                          {reihenfolge.map(gruppe => (
                            <div key={gruppe} className="za-felder-section">
                              <div className="za-felder-section-title">{gruppe}</div>
                              <div className="za-felder-grid">
                                {gruppen[gruppe].map(f => {
                                  const wert = (tagEdit.extra || {})[f.feld_name];
                                  if ((f.feld_typ || "checkbox") === "text") {
                                    return (
                                      <label key={f.id} className="za-feld-text">
                                        <span>{f.feld_label || f.feld_name}</span>
                                        <input type="text" className="za-input" value={wert || ""}
                                          onChange={ev => setzen(f.feld_name, ev.target.value)} />
                                      </label>
                                    );
                                  }
                                  return (
                                    <label key={f.id} className="za-bereich-check">
                                      <input type="checkbox" checked={!!wert}
                                        onChange={ev => setzen(f.feld_name, ev.target.checked)} />
                                      {f.feld_label || f.feld_name}
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </>
                )}

                <div className="za-field">
                  <label className="za-label">Tätigkeit</label>
                  <textarea className="za-input za-blatt-was" rows={2} value={tagEdit.was}
                    onChange={ev => setTagEdit(t => ({ ...t, was: ev.target.value }))} />
                </div>

                </div>

                <div className="za-blatt-aktionen">
                  <button className="za-blatt-loeschen" disabled={!!tagBusy} onClick={tagLeeren}>
                    {tagBusy === "loeschen" ? "…" : "Leeren"}
                  </button>
                  <button className="za-blatt-speichern" disabled={!!tagBusy} onClick={tagSpeichern}>
                    {tagBusy === "speichern" ? "Speichert…" : "Speichern"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Anträge (Urlaub/Krank) ── */}
      {tab === "antraege" && (
        <div className="za-content">
          <div className="za-stat-card">
            <div className="za-stat-title">Neuer Urlaubsantrag</div>
            <div className="za-antrag-form">
              <div className="za-antrag-daterow">
                <label className="za-antrag-label">Von
                  <input type="date" className="za-input" value={antrForm.von}
                    onChange={e => setAntrForm(f => ({ ...f, von: e.target.value }))} />
                </label>
                <label className="za-antrag-label">Bis
                  <input type="date" className="za-input" value={antrForm.bis}
                    onChange={e => setAntrForm(f => ({ ...f, bis: e.target.value }))} />
                </label>
              </div>
              <label className="za-antrag-label">Anmerkung (optional)
                <input type="text" className="za-input" value={antrForm.anmerkung} placeholder="z. B. Sommerurlaub"
                  onChange={e => setAntrForm(f => ({ ...f, anmerkung: e.target.value }))} />
              </label>
              <button className="za-btn za-btn-save" disabled={antrSaving} onClick={submitAntrag}>
                {antrSaving ? "…" : "Antrag stellen"}
              </button>
              <div className="za-antrag-hint">
                Krank? Das trägst du unter „Heute" ein — bei ART einfach „Krank" statt „Arbeit" wählen.
              </div>
            </div>
          </div>

          <h3 className="za-month-title">Meine Anträge</h3>
          {antraege.length === 0 && <p className="za-empty">Keine Anträge</p>}
          {antraege.map(a => (
            <div key={a.id} className="za-stat-card">
              <div className="za-stat-row">
                <span>{a.art === "Urlaub" ? "🏖 Urlaub" : "🤒 Krank"}</span>
                <span className={`za-antrag-status za-antrag-${a.status}`}>{a.status}</span>
              </div>
              <div className="za-stat-row">
                <span>{(a.von || "").slice(0, 10)} – {(a.bis || "").slice(0, 10)}</span>
                <span>{a.werktage != null ? `${a.werktage} Werktage` : ""}</span>
              </div>
              {a.anmerkung && <div className="za-tag-was">{a.anmerkung}</div>}
              {a.status === "offen" && (
                <button className="za-btn za-btn-outline za-antrag-zurueck" onClick={() => zurueckziehenAntrag(a.id)}>
                  Zurückziehen
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Tab: Statistik ── */}
      {tab === "statistik" && (
        <div className="za-content">
          {/* Kalender-Abo (ICS) — iOS/Mac: per Klick System-Kalender öffnen */}
          {maToken && (() => {
            const httpsUrl = `${API_BASE}/arbeitsnachweis/calendar.ics?token=${encodeURIComponent(maToken)}`;
            const webcal = httpsUrl.replace(/^https?:\/\//, "webcal://");
            return (
              <div className="za-cal-box">
                <div className="za-cal-title">📅 Kalender abonnieren</div>
                <div className="za-cal-actions">
                  <a className="za-btn za-btn-save" href={webcal}>In Kalender öffnen</a>
                  <button className="za-btn za-btn-outline"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(httpsUrl);
                        showToast("URL kopiert ✓");
                      } catch { window.prompt("Diese URL kopieren:", httpsUrl); }
                    }}>
                    URL kopieren
                  </button>
                </div>
                <div className="za-cal-hint">Token-gesichert — wie ein Passwort behandeln.</div>
              </div>
            );
          })()}
          {/* Saldo-Block: Soll/Ist + Resturlaub + Erklärung */}
          {stundensaldo && (
            <div className="za-stat-card za-saldo-card">
              <div className="za-stat-title">Saldo {stundensaldo.jahr}</div>
              <div className="za-stat-row">
                <span>Stundensaldo</span>
                <span className={stundensaldo.saldo_h >= 0 ? "za-saldo-pos" : "za-saldo-neg"}>
                  {stundensaldo.saldo_h >= 0 ? "+" : ""}{stundensaldo.saldo_h.toFixed(1)} h
                </span>
              </div>
              <div className="za-stat-row">
                <span>Soll · Ist</span>
                <span>{stundensaldo.soll_h.toFixed(0)} h · {stundensaldo.ist_h.toFixed(0)} h</span>
              </div>
              <div className="za-stat-row">
                <span>Urlaub Rest</span>
                <span className="za-urlaub">
                  {stundensaldo.urlaub_rest} / {stundensaldo.urlaubstage_pro_jahr} Tage
                </span>
              </div>
              {stundensaldo.krank_tage > 0 && (
                <div className="za-stat-row">
                  <span>Krank</span>
                  <span className="za-krank">{stundensaldo.krank_tage} Tage</span>
                </div>
              )}
              <div className="za-saldo-erklaerung">{stundensaldo.erklaerung}</div>
            </div>
          )}

          <div className="za-filter-row">
            <select className="za-select" value={jahr} onChange={e => setJahr(+e.target.value)}>
              {[2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {jahresuebersicht.map(j => (
            <div key={j.jahr} className="za-stat-card">
              <div className="za-stat-title">{j.jahr}</div>
              <div className="za-stat-row"><span>Stunden</span><span>{j.stunden?.toFixed(0)} h</span></div>
              <div className="za-stat-row"><span>Arbeitstage</span><span>{j.arbeitstage}</span></div>
              {/* Krank/Urlaub nur zeigen wenn > 0 — sonst kein 0-Geräusch. */}
              {j.krank_tage > 0 && (
                <div className="za-stat-row"><span>Krank</span><span className="za-krank">{j.krank_tage} Tage</span></div>
              )}
              {j.urlaub_tage > 0 && (
                <div className="za-stat-row"><span>Urlaub</span><span className="za-urlaub">{j.urlaub_tage} Tage</span></div>
              )}
            </div>
          ))}

          <h3 className="za-month-title">Monatsübersicht {jahr}</h3>
          {monate.map(m => (
            <div key={`${m.jahr}-${m.monat}`} className="za-month-row">
              <span className="za-month-label">{MONTHS_DE[m.monat - 1]}</span>
              <span>{m.stunden?.toFixed(1)} h</span>
              <span>{m.arbeitstage} AT</span>
              {m.krank_tage > 0 && <span className="za-krank">{m.krank_tage} K</span>}
              {m.urlaub_tage > 0 && <span className="za-urlaub">{m.urlaub_tage} U</span>}
            </div>
          ))}
        </div>
      )}

      {toast && (
        <div className={`za-toast za-toast-${toast.type}`}>{toast.msg}</div>
      )}
    </div>
  );
}
